import React, { useState, useEffect } from "react"
import "./PurchaseModal.scss"
import { bookProduct } from "../../queries/bookProduct"

const PurchaseModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [bookData, setBookData] = useState(null)
  const [fetchError, setFetchError] = useState(null)

  // Fetch book product info on mount
  useEffect(() => {
    const fetchBookData = async () => {
      try {
        if (!process.env.REACT_APP_API) {
          throw new Error("API endpoint not configured (REACT_APP_API)")
        }

        const response = await fetch(process.env.REACT_APP_API, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            query: bookProduct
          })
        })

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()

        // Check for GraphQL errors
        if (data.errors) {
          console.error("GraphQL errors:", data.errors)
          throw new Error(data.errors[0]?.message || "GraphQL error")
        }

        if (data.data?.products && data.data.products.length > 0) {
          setBookData(data.data.products[0])
        } else {
          setFetchError("Could not load product information")
        }
      } catch (err) {
        console.error("Error fetching book product:", err.message || err)
        setFetchError(err.message || "Could not load product information")
      }
    }

    fetchBookData()
  }, [])

  const bookPrice = bookData?.variants?.[0]?.price || 29.99
  const bookTitle = bookData?.title || "Book Access"

  const handlePurchase = async () => {
    setError(null)
    setLoading(true)

    try {
      // Add book product to cart
      const response = await fetch(
        `${process.env.REACT_APP_API?.replace('/api', '') || 'http://lac.local'}/actions/my-module/cart/add-to-cart`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          credentials: "include",
          body: new URLSearchParams({
            productHandle: "book",
            qty: 1,
          })
        }
      )

      const data = await response.json()

      if (!response.ok || data.error) {
        setError(data.error || "Failed to add to cart")
        setLoading(false)
        return
      }

      // Store cart number in localStorage so checkout can retrieve the same cart
      if (data.cart?.number) {
        localStorage.setItem('cartNumber', data.cart.number)
        console.log('Cart created with number:', data.cart.number)
      }

      // Redirect to checkout
      window.location.href = "/checkout"
    } catch (err) {
      console.error("Purchase error:", err)
      setError("An error occurred. Please try again.")
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      {/* Modal Overlay */}
      <div className="purchase-overlay" onClick={onClose} />

      {/* Modal Content */}
      <div className="purchase-modal">
        <div className="modal-content">
          {/* Close Button */}
          <button className="close-button" onClick={onClose} aria-label="Close">
            ×
          </button>

          {/* Header */}
          <div className="modal-header">
            <h2>Get Book Access</h2>
            <p className="subtitle">Unlock full access to the {bookTitle}</p>
          </div>

          {/* Body */}
          <div className="modal-body">
            {fetchError && (
              <div className="purchase-error">
                {fetchError}
              </div>
            )}

            {!fetchError && (
              <>
                <div className="price-section">
                  <div className="price-label">One-time purchase</div>
                  <div className="price-amount">${bookPrice.toFixed(2)}</div>
                </div>

                <div className="features-list">
                  <h3>What you'll get:</h3>
                  <ul>
                    <li>✓ Full access to the book content</li>
                    <li>✓ Download and reference chapters</li>
                    <li>✓ Searchable text</li>
                  </ul>
                </div>
              </>
            )}

            {error && (
              <div className="purchase-error">
                Something went wrong. Please try again.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              className="button secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="button defaultRed"
              onClick={handlePurchase}
              disabled={loading}
            >
              {loading ? "Processing..." : "Purchase Now"}
            </button>
          </div>
        </div>
      </div>
    </>
  )
}

export default PurchaseModal
